import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { getRequest } from '../../api/ApiController';
import UtilContext from '../../components/UtilContext';
import { LocaleContext } from '../../components/LocaleContext';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import countries from 'i18n-iso-countries';
import 'i18n-iso-countries/langs/sk';
import 'i18n-iso-countries/langs/en';
import 'i18n-iso-countries/langs/de';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import { customerClientTypeDisplayLabel } from '../../utils/customerClientTypeDisplayLabel';
import CrmCommunicationTicketsList, {
  CRM_COMM_MASK_MY_AGENDA,
} from './CrmCommunicationTicketsList';
import {
  CRM_COMM_PAGE_MY_BUSINESS_BY_CUSTOMER_DETAIL_QUOTES,
  CRM_COMM_PAGE_MY_ACQUISITION_BY_CUSTOMER_DETAIL_ACQUISITIONS,
} from '../../utils/crmCommunicationTableColumns';
import { CRM_COMM_TICKETS_STATUS_VALUES } from '../../utils/crmCommunicationTicketFilters';
import { PrintIcon, RefreshIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon } from '../svg/svgIcons';
import { createArrayNavigateHandlers } from '../../utils/arrayNavigate';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';

const PAGE_ADD_KEY = 'CustomerMyBusinessByCustomerDetail';
countries.registerLocale(require('i18n-iso-countries/langs/sk'));
countries.registerLocale(require('i18n-iso-countries/langs/en'));
countries.registerLocale(require('i18n-iso-countries/langs/de'));

function normalizeInitialValues(row) {
  const types = Array.isArray(row?.types) ? row.types : [];
  const statusByType = row?.partner_status_by_type && typeof row.partner_status_by_type === 'object'
    ? row.partner_status_by_type
    : {};
  const partyMode = row?.party_mode
    || statusByType?.party_mode
    || (types.includes('customer') ? 'customer' : (types.includes('client') ? 'client' : 'none'));
  return {
    id: row?.id || null,
    name: row?.name || '',
    defined_id: row?.defined_id || '',
    country: row?.country || '',
    address: row?.address || '',
    tel_number: row?.tel_number || '',
    email: row?.email || '',
    web_address: row?.web_address || '',
    contact_person: row?.contact_person || '',
    description: row?.description || '',
    types,
    party_mode: partyMode,
    customer_state: row?.customer_state || 'free',
    trader_id: row?.trader_id || null,
    trader_name: row?.trader_name || '',
    customer_type: row?.customer_type || statusByType?.customer || '',
    client_type: row?.client_type || statusByType?.client || '',
  };
}

function countryLabelFromCode(countryCode, locale) {
  if (!countryCode) return '';
  const lang = locale === 'de' ? 'de' : (locale === 'en' || locale === 'gb' ? 'en' : 'sk');
  return countries.getName(countryCode, lang) || countryCode;
}

export default function CustomerMyBusinessByCustomerDetail({
  customerId,
  customerRow = null,
  type = 'edit',
  adminMode = false,
  mode = 'quotes',
  listEndpointBase = '/api/customer_evaluations/my_business/by_customer',
  detailEndpointPrefix = '/api/customer_evaluations/my_business/customers',
  embeddedListPageKey = mode === 'acquisitions'
    ? CRM_COMM_PAGE_MY_ACQUISITION_BY_CUSTOMER_DETAIL_ACQUISITIONS
    : CRM_COMM_PAGE_MY_BUSINESS_BY_CUSTOMER_DETAIL_QUOTES,
}) {
  const { t, locale } = useContext(LocaleContext);
  const { toast, openFavoriteDialog, setFastView } = useContext(UtilContext);
  const [formType] = useState(type);
  const [allRows, setAllRows] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const navListBootstrappedRef = useRef(false);

  const customerClientTypeOptions = useMemo(() => ([
    { value: 'new_client', label: t('CustomerTypeOptions.NewClient') },
    { value: 'potential_client', label: t('CustomerTypeOptions.PotentialClient') },
    { value: 'verified_client', label: t('CustomerTypeOptions.VerifiedClient') },
    { value: 'inquiring_client', label: t('CustomerTypeOptions.InquiringClient') },
    { value: 'occasional_customer', label: t('CustomerTypeOptions.OccasionalCustomer') },
    { value: 'regular_customer', label: t('CustomerTypeOptions.RegularCustomer') },
    { value: 'strategic_customer', label: t('CustomerTypeOptions.StrategicCustomer') },
    { value: 'lost_customer', label: t('CustomerTypeOptions.LostCustomer') },
  ]), [t]);
  const customerTypeValues = useMemo(() => new Set(['occasional_customer', 'regular_customer', 'strategic_customer', 'lost_customer']), []);
  const clientTypeValues = useMemo(() => new Set(['new_client', 'potential_client', 'verified_client', 'inquiring_client']), []);
  const customerTypeOptions = useMemo(
    () => customerClientTypeOptions.filter((opt) => customerTypeValues.has(opt.value)),
    [customerClientTypeOptions, customerTypeValues],
  );
  const clientTypeOptions = useMemo(
    () => customerClientTypeOptions.filter((opt) => clientTypeValues.has(opt.value)),
    [customerClientTypeOptions, clientTypeValues],
  );

  const embeddedTicketTypes = mode === 'acquisitions' ? 'acquisition' : 'quote_request';
  const embeddedTabLabel = mode === 'acquisitions'
    ? t('CustomerMyAcquisitionByCustomerDetail.Tab.Acquisitions')
    : t('CustomerMyCustomersDetail.Tab.InquiriesOffers');

  const formik = useFormik({
    initialValues: normalizeInitialValues(customerRow),
    enableReinitialize: false,
    onSubmit: () => {},
  });

  const displayCustomerType = useMemo(() => {
    if (formik.values.party_mode === 'client') return '';
    return formik.values.customer_type || '';
  }, [formik.values.customer_type, formik.values.party_mode]);

  const displayClientType = useMemo(() => {
    if (formik.values.party_mode === 'customer') return '';
    return formik.values.client_type || '';
  }, [formik.values.client_type, formik.values.party_mode]);

  const loadRows = useCallback(async () => {
    const rowsResp = await getRequest(`${listEndpointBase}/paged?size=200`);
    setAllRows(Array.isArray(rowsResp?.data) ? rowsResp.data : []);
  }, [listEndpointBase]);

  const ensureNavListLoaded = useCallback(async () => {
    if (navListBootstrappedRef.current) return;
    navListBootstrappedRef.current = true;
    try {
      await loadRows();
    } catch (_) {
      navListBootstrappedRef.current = false;
    }
  }, [loadRows]);

  const detailShowPath = adminMode
    ? '/api/customer_evaluations/my_customers'
    : detailEndpointPrefix;

  const loadDetail = useCallback(async (id) => {
    if (!id) return null;
    const resp = await getRequest(`${detailShowPath}/${id}`);
    const payload = resp?.data ?? resp;
    return payload?.data ?? payload ?? null;
  }, [detailShowPath]);

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    const run = async () => {
      const hasRowFromList = !!customerRow && Number(customerRow?.id) === Number(customerId);
      if (hasRowFromList) {
        formik.setValues(normalizeInitialValues(customerRow));
      }
      setDetailLoading(true);
      try {
        const row = await loadDetail(customerId);
        if (!active) return;
        formik.setValues(normalizeInitialValues(row || customerRow || null));
        const scheduleNavList = typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
          ? (cb) => window.requestIdleCallback(cb, { timeout: 2500 })
          : (cb) => window.setTimeout(cb, 400);
        scheduleNavList(() => {
          if (!active) return;
          ensureNavListLoaded().catch(() => {});
        });
      } catch (e) {
        toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('Common.LoadFailed'), life: 3000 });
      } finally {
        if (active) setDetailLoading(false);
      }
    };
    run();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, customerRow, loadDetail, t, toast]);

  const navRows = useMemo(
    () => (allRows || [])
      .filter((r) => String(r?.row_kind || 'company') !== 'ticket' && r?.id != null)
      .map((r) => ({ id: r.id, name: r.name })),
    [allRows],
  );
  const nav = useMemo(() => createArrayNavigateHandlers(navRows, formik.values?.id || null), [navRows, formik.values?.id]);
  const navDisabled = !formik.values?.id || navRows.length < 2;

  const openById = useCallback(async (id) => {
    if (!id) return;
    setDetailLoading(true);
    try {
      await ensureNavListLoaded();
      const row = await loadDetail(id);
      if (row) formik.setValues(normalizeInitialValues(row));
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('Common.LoadFailed'), life: 3000 });
    } finally {
      setDetailLoading(false);
    }
  }, [ensureNavListLoaded, formik, loadDetail, t, toast]);

  const reloadCurrent = async () => {
    if (!formik.values?.id) return;
    setDetailLoading(true);
    try {
      const row = await loadDetail(formik.values.id);
      if (row) formik.setValues(normalizeInitialValues(row));
      toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('Common.DataReloaded'), life: 2500 });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: t('Common.LoadFailed'), life: 3000 });
    } finally {
      setDetailLoading(false);
    }
  };

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );
  const closeMenu = () => setExpandedKeys({});
  const items = [{
    key: '0',
    label: t('ServiceTask.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('Common.RefreshDetail'), icon: 'pi pi-refresh', command: () => { reloadCurrent(); closeMenu(); }, template: itemRenderer },
    ],
  }];

  return (
    <form onSubmit={formik.handleSubmit} className="h-full min-h-0 flex flex-col overflow-hidden">
      <div className="w-full h-10 flex flex-row bg-[#333] shrink-0">
        <IntersectionObserverWrapper>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Common.Print')} onClick={() => window.print()}><PrintIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Common.Refresh')} onClick={reloadCurrent}><RefreshIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={() => { const first = nav.first(); if (first?.id) openById(first.id); }}><BackToFirstIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={() => { const prev = nav.prev(); if (prev?.id) openById(prev.id); }}><BackIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={() => { const next = nav.next(); if (next?.id) openById(next.id); }}><ForwardIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={() => { const last = nav.last(); if (last?.id) openById(last.id); }}><ForwardToLastIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Common.AddToFavorites')} onClick={() => openFavoriteDialog?.(t('CustomerMyBusinessByCustomerDetail.Title'), PAGE_ADD_KEY)}><FavoriteIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Common.QuickView')} onClick={() => setFastView?.(PAGE_ADD_KEY)}><PriceIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="card flex justify-content-center z-[100]" data-targetid="actions">
          <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
        </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="relative flex-1 min-h-0 min-w-0 overflow-hidden">
        {detailLoading ? (
          <div className="absolute inset-x-0 top-0 z-10 px-4 py-2 text-sm text-gray-600 border-b border-gray-200 bg-white/90">{t('Common.LoadingData')}</div>
        ) : null}
        <Splitter
          className="h-full"
          layout="vertical"
          pt={{ gutter: { className: 'bg-[#333] h-8' }, gutterHandler: { className: 'bg-secondary' } }}
        >
          <SplitterPanel className="flex align-items-center justify-content-center min-h-0 min-w-0 overflow-x-auto overflow-y-auto" size={80} minSize={20}>
            <div className="w-full min-w-0 px-1 py-1 mb-2">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="w-auto p-6 m-1 bg-gray-100 rounded-md shadow-md">
                  <h1 className="text-3xl font-semibold text-left text-zinc-900">{t('CustomerMyBusinessByCustomerDetail.Title')}</h1>
                  {formType === 'edit' && <div className="text-md font-semibold text-gray-800 mt-1">{t('Common.RecordIdLabel')}: {formik.values.id}</div>}
                  <div className="mt-4">
                    <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Name')}</label>
                    <input disabled value={formik.values.name} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 md:gap-6 gap-4 mt-4">
                    <div className="md:col-span-4 min-w-0">
                      <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.DefinedId')}</label>
                      <input disabled value={formik.values.defined_id || ''} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                      <p className="text-xs text-gray-500 mt-1">{t('BusinessPartnerAdd.DefinedIdHint')}</p>
                    </div>
                    <div className="md:col-span-8 min-w-0">
                      <label className="block text-md font-semibold text-gray-800">{t('CustomerCentralDatabase.Label.PartnerType')}</label>
                      <div className="grid grid-cols-2 py-2 bg-white border rounded-md mt-2 px-3">
                        <div className="flex align-items-center"><Checkbox checked={formik.values.types?.includes('supplier')} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" /><label className="ml-2">{t('BusinessPartnerAdd.Type.Supplier')}</label></div>
                        <div className="flex align-items-center"><Checkbox checked={formik.values.types?.includes('calibration_company')} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" /><label className="ml-2">{t('BusinessPartnerAdd.Type.CalibrationCompany')}</label></div>
                        <div className="flex align-items-center"><Checkbox checked={formik.values.types?.includes('training_company')} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" /><label className="ml-2">{t('BusinessPartnerAdd.Type.TrainingCompany')}</label></div>
                        <div className="flex align-items-center"><Checkbox checked={formik.values.types?.includes('service_company')} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" /><label className="ml-2">{t('BusinessPartnerAdd.Type.ServiceCompany')}</label></div>
                        <div className="flex align-items-center"><Checkbox checked={formik.values.party_mode === 'customer'} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" /><label className="ml-2">{t('BusinessPartnerAdd.Type.Customer')}</label></div>
                        <div className="flex align-items-center"><Checkbox checked={formik.values.party_mode === 'client'} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" /><label className="ml-2">{t('BusinessPartnerAdd.Type.Client')}</label></div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 p-3 bg-white border rounded-md">
                    <div className="grid grid-cols-1 md:grid-cols-12 md:gap-3 gap-2">
                      <div className="md:col-span-4 min-w-0">
                        <div>
                          <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.ReadOnly.CustomerState')}</label>
                          <div className="mt-2 p-2 bg-white border rounded-md grid grid-cols-1 gap-1">
                            <div className="flex align-items-center">
                              <Checkbox inputId="mb_detail_customer_state_occupied" checked={formik.values.customer_state === 'occupied'} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                              <label htmlFor="mb_detail_customer_state_occupied" className="ml-2">{t('CustomerCentralDatabase.State.Occupied')}</label>
                            </div>
                            <div className="flex align-items-center">
                              <Checkbox inputId="mb_detail_customer_state_free" checked={formik.values.customer_state !== 'occupied'} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                              <label htmlFor="mb_detail_customer_state_free" className="ml-2">{t('CustomerCentralDatabase.State.Free')}</label>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="md:col-span-8 min-w-0">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:pl-1">
                          <div className="min-w-0">
                            <label className="block text-md font-semibold text-gray-800">{t('CustomerMyBusinessByCustomerDetail.Label.CustomerType')}</label>
                            <div className="mt-2 p-2 bg-white border rounded-md grid grid-cols-1 gap-1">
                              {customerTypeOptions.map((opt) => (
                                <div key={`mb_customer_type_${opt.value}`} className="flex align-items-center">
                                  <Checkbox inputId={`mb_customer_type_${opt.value}`} checked={displayCustomerType === opt.value} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                                  <label htmlFor={`mb_customer_type_${opt.value}`} className="ml-2">{customerClientTypeDisplayLabel(opt.label, locale)}</label>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="min-w-0 sm:pl-1">
                            <label className="block text-md font-semibold text-gray-800">{t('CustomerMyBusinessByCustomerDetail.Label.ClientType')}</label>
                            <div className="mt-2 p-2 bg-white border rounded-md grid grid-cols-1 gap-1">
                              {clientTypeOptions.map((opt) => (
                                <div key={`mb_client_type_${opt.value}`} className="flex align-items-center">
                                  <Checkbox inputId={`mb_client_type_${opt.value}`} checked={displayClientType === opt.value} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                                  <label htmlFor={`mb_client_type_${opt.value}`} className="ml-2">{customerClientTypeDisplayLabel(opt.label, locale)}</label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.ReadOnly.Trader')}</label>
                      <select disabled value={formik.values.trader_id || ''} className="block w-full h-10 px-3 py-2 mt-2 border rounded-md bg-white">
                        <option value="">{t('CustomerCentralDatabaseAdd.SelectTrader')}</option>
                        <option value={formik.values.trader_id || ''}>{formik.values.trader_name || ''}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="w-auto p-6 m-1 bg-gray-100 rounded-md shadow-md">
                  <div>
                    <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.ContactPerson')}</label>
                    <input disabled value={formik.values.contact_person} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                  </div>
                  <div>
                    <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Country')}</label>
                    <input disabled value={countryLabelFromCode(formik.values.country, locale)} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                  </div>
                  <div>
                    <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Address')}</label>
                    <input disabled value={formik.values.address} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                  </div>
                  <div>
                    <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Phone')}</label>
                    <input disabled value={formik.values.tel_number} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                  </div>
                  <div>
                    <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Email')}</label>
                    <input disabled value={formik.values.email} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                  </div>
                  <div>
                    <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.WebAddress')}</label>
                    <input disabled value={formik.values.web_address} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                  </div>
                  <div>
                    <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Note')}</label>
                    <textarea disabled value={formik.values.description} className="block w-full min-h-[84px] px-4 py-2 mt-2 border rounded-md bg-white" />
                  </div>
                </div>
              </div>
            </div>
          </SplitterPanel>
          <SplitterPanel className="flex flex-col min-h-0 min-w-0 overflow-hidden" size={55} minSize={25} style={{ minHeight: '110px' }}>
            <div className="m-1 bg-white border border-gray-300 rounded-md shadow-sm overflow-hidden h-full flex flex-col flex-1 min-h-0 w-full">
              <div className="px-3 py-2 border-b border-gray-200 text-sm font-semibold text-gray-800 shrink-0">
                {embeddedTabLabel}
              </div>
              <div className="flex-1 min-h-0 min-w-0">
                {formik.values.id ? (
                  <CrmCommunicationTicketsList
                    embedded
                    embeddedFillParent
                    crmTicketsLayout
                    mineOnlyDefault={false}
                    traderBusinessScope={!adminMode}
                    maskCode={CRM_COMM_MASK_MY_AGENDA}
                    companyId={formik.values.id}
                    types={embeddedTicketTypes}
                    listPageKey={embeddedListPageKey}
                    defaultStatusValue={CRM_COMM_TICKETS_STATUS_VALUES.ALL}
                  />
                ) : null}
              </div>
            </div>
          </SplitterPanel>
        </Splitter>
      </div>
    </form>
  );
}
